/**
 * Rate Limiting Store Module
 *
 * Manages rate limiting state and provides functionality for:
 * - Tracking rate limit status per endpoint
 * - Managing request queues
 * - Countdown timers
 * - User notifications
 * - Backward compatibility with global rate limiting
 */

import { getEndpointDisplayName, getEndpointPriority, ENDPOINT_DISPLAY_NAMES } from "@/utils/rateLimitUtils";

const state = {
	// Global rate limiting state (for backward compatibility)
	isRateLimited: false,
	isLockedOut: false,
	lockoutDuration: null,
	violatedLimit: null,
	retryAfter: null,
	remainingTime: 0,
	warningThreshold: false,
	requestsRemaining: null,
	requestLimit: null,
	queuedRequests: [],
	rateLimitMessage: "",
	resetTimestamp: null,
	countdownInterval: null,
	lastRateLimitTrigger: null,

	// NEW: Per-endpoint rate limiting
	endpoints: {},
	// Structure for each endpoint:
	// {
	//   "post_/api/search/product": {
	//     isRateLimited: true,
	//     isLockedOut: true,
	//     lockoutDuration: 45,
	//     violatedLimit: "5 per 1s",
	//     retryAfter: 45,
	//     remainingTime: 32,
	//     resetTimestamp: 1234567890,
	//     lastTrigger: 1234567890,
	//     intervalId: null,
	//     requestsRemaining: null,
	//     requestLimit: null,
	//     queuedRequests: []
	//   }
	// }

	// Configuration
	endpointDisplayNames: ENDPOINT_DISPLAY_NAMES,
	maxVisibleNotifications: 3,
	dismissedEndpoints: {},
};

const getters = {
	// Global getters (for backward compatibility)
	isRateLimited: (state) => state.isRateLimited,
	isLockedOut: (state) => state.isLockedOut,
	lockoutDuration: (state) => state.lockoutDuration,
	violatedLimit: (state) => state.violatedLimit,
	remainingTime: (state) => state.remainingTime,
	lastRateLimitTrigger: (state) => state.lastRateLimitTrigger,
	progressPercentage: (state) => {
		if (!state.retryAfter || state.retryAfter === 0) return 100;
		return Math.max(0, Math.min(100, ((state.retryAfter - state.remainingTime) / state.retryAfter) * 100));
	},
	formattedTime: (state) => {
		const minutes = Math.floor(state.remainingTime / 60);
		const seconds = state.remainingTime % 60;
		if (minutes > 0) {
			return `${minutes} minute${minutes !== 1 ? "s" : ""} ${seconds} second${seconds !== 1 ? "s" : ""}`;
		}
		return `${seconds} second${seconds !== 1 ? "s" : ""}`;
	},
	isWarning: (state) => state.warningThreshold,
	requestsRemaining: (state) => state.requestsRemaining,
	hasQueuedRequests: (state) => state.queuedRequests.length > 0,
	queuedRequestCount: (state) => state.queuedRequests.length,
	rateLimitMessage: (state) => {
		if (state.isLockedOut && state.violatedLimit) {
			return `Rate limit exceeded (${state.violatedLimit}). Account locked for ${state.lockoutDuration} seconds.`;
		}
		return (
			state.rateLimitMessage ||
			"You have exceeded the maximum number of requests. Please wait before trying again."
		);
	},

	// NEW: Endpoint-specific getters
	allRateLimitedEndpoints: (state) => {
		return Object.keys(state.endpoints).filter((endpoint) => state.endpoints[endpoint]?.isRateLimited);
	},

	isEndpointRateLimited: (state) => (endpoint) => {
		return state.endpoints[endpoint]?.isRateLimited || false;
	},

	getEndpointRateLimitData: (state) => (endpoint) => {
		return state.endpoints[endpoint] || null;
	},

	getEndpointRemainingTime: (state) => (endpoint) => {
		return state.endpoints[endpoint]?.remainingTime || 0;
	},

	getEndpointDisplayName: (state) => (endpoint) => {
		return getEndpointDisplayName(endpoint, state.endpointDisplayNames);
	},

	endpointDisplayNames: (state) => state.endpointDisplayNames,

	// Get the most severe rate limit across all endpoints
	mostSevereRateLimit: (state) => {
		const rateLimited = Object.values(state.endpoints).filter((e) => e.isRateLimited);
		if (rateLimited.length === 0) return null;

		return rateLimited.reduce((most, current) => {
			if (!most) return current;
			// Prefer locked out over just rate limited
			if (current.isLockedOut && !most.isLockedOut) return current;
			if (!current.isLockedOut && most.isLockedOut) return most;
			// Prefer longer duration
			if (current.lockoutDuration > most.lockoutDuration) return current;
			return most;
		}, null);
	},

	// Check if any endpoint is rate limited
	isAnyEndpointRateLimited: (state) => {
		return Object.values(state.endpoints).some((e) => e.isRateLimited);
	},

	// Get visible notifications (not dismissed)
	visibleRateLimitedEndpoints: (state) => {
		return Object.keys(state.endpoints)
			.filter((endpoint) => state.endpoints[endpoint]?.isRateLimited && !state.dismissedEndpoints[endpoint])
			.sort((a, b) => {
				const aData = state.endpoints[a];
				const bData = state.endpoints[b];

				// Sort by locked out first
				if (aData.isLockedOut !== bData.isLockedOut) {
					return aData.isLockedOut ? -1 : 1;
				}

				// Then by lockout duration
				if (aData.lockoutDuration !== bData.lockoutDuration) {
					return (bData.lockoutDuration || 0) - (aData.lockoutDuration || 0);
				}

				// Then by priority
				return getEndpointPriority(a) - getEndpointPriority(b);
			});
	},
};

const mutations = {
	// Global mutations (for backward compatibility)
	SET_RATE_LIMITED(state, payload) {
		state.isRateLimited = payload.limited;
		state.isLockedOut = payload.isLockedOut || false;
		state.lockoutDuration = payload.lockoutDuration || null;
		state.violatedLimit = payload.violatedLimit || null;
		state.retryAfter = payload.retryAfter || null;
		state.resetTimestamp = payload.resetTimestamp || null;
		state.rateLimitMessage = payload.message || "";
		state.lastRateLimitTrigger = Date.now();

		if (payload.limited && payload.retryAfter) {
			state.remainingTime = payload.retryAfter;
		} else {
			state.remainingTime = 0;
		}
	},

	UPDATE_REMAINING_TIME(state, time) {
		state.remainingTime = Math.max(0, time);
		if (state.remainingTime === 0) {
			state.isRateLimited = false;
		}
	},

	SET_WARNING_THRESHOLD(state, isWarning) {
		state.warningThreshold = isWarning;
	},

	UPDATE_REQUEST_LIMITS(state, payload) {
		if (payload.remaining !== undefined) {
			state.requestsRemaining = payload.remaining;
			state.requestLimit = payload.limit || null;

			if (state.requestLimit && state.requestsRemaining <= Math.ceil(state.requestLimit * 0.2)) {
				state.warningThreshold = true;
			} else {
				state.warningThreshold = false;
			}
		}
	},

	QUEUE_REQUEST(state, request) {
		state.queuedRequests.push(request);
	},

	REMOVE_FROM_QUEUE(state, requestId) {
		state.queuedRequests = state.queuedRequests.filter((r) => r.id !== requestId);
	},

	CLEAR_QUEUE(state) {
		state.queuedRequests = [];
	},

	SET_COUNTDOWN_INTERVAL(state, intervalId) {
		if (state.countdownInterval) {
			clearInterval(state.countdownInterval);
		}
		state.countdownInterval = intervalId;
	},

	CLEAR_COUNTDOWN_INTERVAL(state) {
		if (state.countdownInterval) {
			clearInterval(state.countdownInterval);
			state.countdownInterval = null;
		}
	},

	// NEW: Endpoint-specific mutations
	SET_ENDPOINT_RATE_LIMITED(state, { endpoint, ...payload }) {
		if (!state.endpoints[endpoint]) {
			state.endpoints[endpoint] = {
				isRateLimited: false,
				isLockedOut: false,
				lockoutDuration: null,
				violatedLimit: null,
				retryAfter: null,
				remainingTime: 0,
				resetTimestamp: null,
				lastTrigger: null,
				intervalId: null,
				requestsRemaining: null,
				requestLimit: null,
				queuedRequests: [],
			};
		}

		const endpointData = state.endpoints[endpoint];
		endpointData.isRateLimited = true;
		endpointData.isLockedOut = payload.isLockedOut || false;
		endpointData.lockoutDuration = payload.lockoutDuration || null;
		endpointData.violatedLimit = payload.violatedLimit || null;
		endpointData.retryAfter = payload.retryAfter || null;
		endpointData.resetTimestamp = payload.resetTimestamp || null;
		endpointData.message = payload.message || "";
		endpointData.lastTrigger = Date.now();

		if (payload.retryAfter) {
			endpointData.remainingTime = payload.retryAfter;
		}

		// Force reactivity
		state.endpoints = { ...state.endpoints };
	},

	UPDATE_ENDPOINT_REMAINING_TIME(state, { endpoint, time }) {
		if (state.endpoints[endpoint]) {
			state.endpoints[endpoint].remainingTime = Math.max(0, time);

			if (state.endpoints[endpoint].remainingTime === 0) {
				state.endpoints[endpoint].isRateLimited = false;
				state.endpoints[endpoint].isLockedOut = false;
			}

			// Force reactivity
			state.endpoints = { ...state.endpoints };
		}
	},

	UPDATE_ENDPOINT_REQUEST_LIMITS(state, { endpoint, remaining, limit }) {
		if (!state.endpoints[endpoint]) {
			state.endpoints[endpoint] = {
				isRateLimited: false,
				isLockedOut: false,
				lockoutDuration: null,
				violatedLimit: null,
				retryAfter: null,
				remainingTime: 0,
				resetTimestamp: null,
				lastTrigger: null,
				intervalId: null,
				requestsRemaining: null,
				requestLimit: null,
				queuedRequests: [],
			};
		}

		if (remaining !== undefined) {
			state.endpoints[endpoint].requestsRemaining = remaining;
			state.endpoints[endpoint].requestLimit = limit || null;
		}

		// Force reactivity
		state.endpoints = { ...state.endpoints };
	},

	SET_ENDPOINT_INTERVAL(state, { endpoint, intervalId }) {
		if (state.endpoints[endpoint]) {
			// Clear existing interval if any
			if (state.endpoints[endpoint].intervalId) {
				clearInterval(state.endpoints[endpoint].intervalId);
			}
			state.endpoints[endpoint].intervalId = intervalId;
		}
	},

	CLEAR_ENDPOINT_RATE_LIMIT(state, endpoint) {
		if (state.endpoints[endpoint]) {
			// Clear interval if exists
			if (state.endpoints[endpoint].intervalId) {
				clearInterval(state.endpoints[endpoint].intervalId);
				state.endpoints[endpoint].intervalId = null;
			}

			// Reset the endpoint state
			state.endpoints[endpoint].isRateLimited = false;
			state.endpoints[endpoint].isLockedOut = false;
			state.endpoints[endpoint].remainingTime = 0;
			state.endpoints[endpoint].queuedRequests = [];

			// Force reactivity
			state.endpoints = { ...state.endpoints };
		}

		// Also remove from dismissed endpoints
		if (state.dismissedEndpoints[endpoint]) {
			delete state.dismissedEndpoints[endpoint];
			state.dismissedEndpoints = { ...state.dismissedEndpoints };
		}
	},

	DISMISS_ENDPOINT_NOTIFICATION(state, endpoint) {
		state.dismissedEndpoints[endpoint] = true;
		state.dismissedEndpoints = { ...state.dismissedEndpoints };
	},

	RESTORE_ENDPOINT_NOTIFICATION(state, endpoint) {
		if (state.dismissedEndpoints[endpoint]) {
			delete state.dismissedEndpoints[endpoint];
			state.dismissedEndpoints = { ...state.dismissedEndpoints };
		}
	},

	UPDATE_GLOBAL_RATE_LIMITED(state) {
		// Update global state based on all endpoints
		const mostSevere = Object.values(state.endpoints)
			.filter((e) => e.isRateLimited)
			.reduce((most, current) => {
				if (!most) return current;
				if (current.isLockedOut && !most.isLockedOut) return current;
				if (!current.isLockedOut && most.isLockedOut) return most;
				if (current.lockoutDuration > most.lockoutDuration) return current;
				return most;
			}, null);

		if (mostSevere) {
			state.isRateLimited = true;
			state.isLockedOut = mostSevere.isLockedOut;
			state.lockoutDuration = mostSevere.lockoutDuration;
			state.violatedLimit = mostSevere.violatedLimit;
			state.retryAfter = mostSevere.retryAfter;
			state.remainingTime = mostSevere.remainingTime;
			state.rateLimitMessage = mostSevere.message;
		} else {
			state.isRateLimited = false;
			state.isLockedOut = false;
			state.lockoutDuration = null;
			state.violatedLimit = null;
			state.retryAfter = null;
			state.remainingTime = 0;
		}
	},
};

const actions = {
	// Global actions (for backward compatibility)
	handleRateLimited({ commit, dispatch }, payload) {
		const retryAfter = parseInt(payload.retryAfter) || 60;
		const resetTimestamp = Date.now() + retryAfter * 1000;

		commit("SET_RATE_LIMITED", {
			limited: true,
			isLockedOut: payload.isLockedOut || false,
			lockoutDuration: payload.lockoutDuration || null,
			violatedLimit: payload.violatedLimit || null,
			retryAfter,
			resetTimestamp,
			message: payload.message,
		});

		dispatch("startCountdown");

		if (payload.request) {
			dispatch("queueRequest", payload.request);
		}
	},

	startCountdown({ commit, state, dispatch }) {
		commit("CLEAR_COUNTDOWN_INTERVAL");

		const intervalId = setInterval(() => {
			const remaining = state.remainingTime - 1;
			commit("UPDATE_REMAINING_TIME", remaining);

			if (remaining <= 0) {
				commit("CLEAR_COUNTDOWN_INTERVAL");
				commit("SET_RATE_LIMITED", { limited: false });

				if (state.queuedRequests.length > 0) {
					dispatch("processQueue");
				}
			}
		}, 1000);

		commit("SET_COUNTDOWN_INTERVAL", intervalId);
	},

	updateLimits({ commit }, payload) {
		commit("UPDATE_REQUEST_LIMITS", payload);
	},

	queueRequest({ commit }, request) {
		const queuedRequest = {
			...request,
			id: Date.now() + Math.random(),
			timestamp: Date.now(),
		};
		commit("QUEUE_REQUEST", queuedRequest);
	},

	async processQueue({ state, commit, dispatch }) {
		if (state.queuedRequests.length === 0) return;

		const request = state.queuedRequests[0];

		try {
			commit("REMOVE_FROM_QUEUE", request.id);
			window.dispatchEvent(new CustomEvent("rate-limit-retry", { detail: request }));

			if (state.queuedRequests.length > 0) {
				setTimeout(() => {
					dispatch("processQueue");
				}, 100);
			}
		} catch (error) {
			console.error("Error processing queued request:", error);
			commit("REMOVE_FROM_QUEUE", request.id);
		}
	},

	clearRateLimit({ commit }) {
		commit("CLEAR_COUNTDOWN_INTERVAL");
		commit("SET_RATE_LIMITED", { limited: false });
		commit("CLEAR_QUEUE");
	},

	reset({ commit }) {
		commit("CLEAR_COUNTDOWN_INTERVAL");
		commit("SET_RATE_LIMITED", { limited: false });
		commit("CLEAR_QUEUE");
		commit("UPDATE_REQUEST_LIMITS", { remaining: null, limit: null });
		commit("SET_WARNING_THRESHOLD", false);
	},

	// NEW: Endpoint-specific actions
	handleEndpointRateLimited({ commit, dispatch }, payload) {
		const { endpoint, ...rateLimitData } = payload;
		const retryAfter = parseInt(rateLimitData.retryAfter) || 60;
		const resetTimestamp = Date.now() + retryAfter * 1000;

		commit("SET_ENDPOINT_RATE_LIMITED", {
			endpoint,
			...rateLimitData,
			retryAfter,
			resetTimestamp,
		});

		// Start countdown for this endpoint
		dispatch("startEndpointCountdown", endpoint);

		// Update global state for backward compatibility
		commit("UPDATE_GLOBAL_RATE_LIMITED");

		// Queue request if provided
		if (rateLimitData.request) {
			dispatch("queueEndpointRequest", { endpoint, request: rateLimitData.request });
		}

		// Restore notification if it was dismissed
		commit("RESTORE_ENDPOINT_NOTIFICATION", endpoint);
	},

	startEndpointCountdown({ commit, state }, endpoint) {
		if (!state.endpoints[endpoint]) return;

		// Clear existing interval if any
		if (state.endpoints[endpoint].intervalId) {
			clearInterval(state.endpoints[endpoint].intervalId);
		}

		const intervalId = setInterval(() => {
			const remaining = (state.endpoints[endpoint]?.remainingTime || 0) - 1;

			commit("UPDATE_ENDPOINT_REMAINING_TIME", { endpoint, time: remaining });

			if (remaining <= 0) {
				clearInterval(intervalId);
				commit("CLEAR_ENDPOINT_RATE_LIMIT", endpoint);
				commit("UPDATE_GLOBAL_RATE_LIMITED");

				// Process queued requests for this endpoint
				if (state.endpoints[endpoint]?.queuedRequests?.length > 0) {
					// Handle queued requests
					const requests = [...state.endpoints[endpoint].queuedRequests];
					state.endpoints[endpoint].queuedRequests = [];

					requests.forEach((request) => {
						window.dispatchEvent(
							new CustomEvent("endpoint-rate-limit-retry", {
								detail: { endpoint, request },
							})
						);
					});
				}
			}
		}, 1000);

		commit("SET_ENDPOINT_INTERVAL", { endpoint, intervalId });
	},

	updateEndpointLimits({ commit }, { endpoint, remaining, limit }) {
		commit("UPDATE_ENDPOINT_REQUEST_LIMITS", { endpoint, remaining, limit });

		// Also update global limits if this is a primary endpoint
		const primaryEndpoints = ["post_/api/search/product", "post_/api/search/formulation"];

		if (primaryEndpoints.includes(endpoint)) {
			commit("UPDATE_REQUEST_LIMITS", { remaining, limit });
		}
	},

	clearEndpointRateLimit({ commit }, endpoint) {
		commit("CLEAR_ENDPOINT_RATE_LIMIT", endpoint);
		commit("UPDATE_GLOBAL_RATE_LIMITED");
	},

	dismissEndpointNotification({ commit }, endpoint) {
		commit("DISMISS_ENDPOINT_NOTIFICATION", endpoint);
	},

	queueEndpointRequest({ state }, { endpoint, request }) {
		if (!state.endpoints[endpoint]) return;

		const queuedRequest = {
			...request,
			id: Date.now() + Math.random(),
			timestamp: Date.now(),
		};

		if (!state.endpoints[endpoint].queuedRequests) {
			state.endpoints[endpoint].queuedRequests = [];
		}

		state.endpoints[endpoint].queuedRequests.push(queuedRequest);
	},

	clearAllEndpointRateLimits({ commit, state }) {
		Object.keys(state.endpoints).forEach((endpoint) => {
			commit("CLEAR_ENDPOINT_RATE_LIMIT", endpoint);
		});
		commit("UPDATE_GLOBAL_RATE_LIMITED");
	},
};

export default {
	namespaced: true,
	state,
	getters,
	mutations,
	actions,
};
